/* eslint-disable react/no-array-index-key */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { Document, Page, pdfjs } from 'react-pdf';
import { useReactToPrint } from 'react-to-print';
import Icons from '../Icons';
import classes from './styles.module.scss';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

import useResizeObserver from '../../../../../hooks/useResizeObserver';

const ACTIVE_MARK_CLASS = 'zf-pdf-mark-active';
const DEFAULT_ZOOM = 1.08;
const ZOOM_LEVELS = [0.6, 0.8, DEFAULT_ZOOM, 1.3, 1.6, 2.0, 2.5, 3.0];
const MIN_ZOOM = ZOOM_LEVELS[0];
const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightPattern(text, pattern) {
  if (!pattern) {
    return text;
  }

  const regExp = new RegExp(escapeRegExp(pattern), 'gi');
  return text.replace(regExp, (value) => `<mark>${value}</mark>`);
}

export default function Pdf({
  isModal,

  resume,
  addCandidateModal,
  deleteResume,
  uploadNewResume,
  formik,
  resumeFieldName,
  isPossibleToEdit,
  isReadOnly,
  isEdit,
  openResumeInput,
}) {
  const [numPages, setNumPages] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagesText, setPagesText] = useState([]);
  const [matches, setMatches] = useState([]);
  const [currentMatchIdx, setCurrentMatchIdx] = useState(-1);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const searchInputRef = useRef(null);
  const pdfRef = useRef(null);
  const miniScrollWindow = useRef(null);
  const mainResumeRef = useRef(null);
  // remember scroll percent when zooming so the same region stays visible
  const pendingScrollPercentRef = useRef(null);

  const { width: mainResumeWidth } = useResizeObserver(mainResumeRef);

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const source = resume?.ur ? resume?.ur : resume;
  const url = resume?.url;

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  }, []);

  const onDocumentLoadSuccess = async (pdf) => {
    setNumPages(pdf.numPages);

    try {
      const texts = [];
      for (let i = 1; i <= pdf.numPages; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(i);
        // eslint-disable-next-line no-await-in-loop
        const content = await page.getTextContent();
        texts.push(content.items.map((item) => item.str).join(' '));
      }
      setPagesText(texts);
    } catch (err) {
      setPagesText([]);
    }
  };

  useEffect(() => {
    if (!searchText || !pagesText.length) {
      setMatches([]);
      setCurrentMatchIdx(-1);
      return;
    }

    const re = new RegExp(escapeRegExp(searchText), 'gi');
    const found = [];
    pagesText.forEach((text, pageIdx) => {
      const pageMatches = text.match(re);
      const count = pageMatches ? pageMatches.length : 0;
      for (let i = 0; i < count; i += 1) {
        found.push({ page: pageIdx + 1, indexOnPage: i });
      }
    });

    setMatches(found);
    setCurrentMatchIdx(found.length > 0 ? 0 : -1);
  }, [searchText, pagesText]);

  useEffect(() => {
    if (currentMatchIdx < 0 || !matches.length) {
      const container = mainResumeRef.current;
      if (container) {
        container
          .querySelectorAll(`.${ACTIVE_MARK_CLASS}`)
          .forEach((el) => el.classList.remove(ACTIVE_MARK_CLASS));
      }
      return undefined;
    }

    const timer = setTimeout(() => {
      const container = mainResumeRef.current;
      if (!container) {
        return;
      }
      const marks = container.querySelectorAll('mark');
      marks.forEach((el) => el.classList.remove(ACTIVE_MARK_CLASS));
      const activeMark = marks[currentMatchIdx];
      if (activeMark) {
        activeMark.classList.add(ACTIVE_MARK_CLASS);
        const containerRect = container.getBoundingClientRect();
        const markRect = activeMark.getBoundingClientRect();
        const offset =
          markRect.top -
          containerRect.top -
          container.clientHeight / 2 +
          markRect.height / 2;
        container.scrollTo({
          top: container.scrollTop + offset,
          behavior: 'smooth',
        });
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [currentMatchIdx, matches, numPages]);

  const goToPrevMatch = useCallback(() => {
    if (!matches.length) return;
    setCurrentMatchIdx((prev) => {
      if (prev <= 0) return matches.length - 1;
      return prev - 1;
    });
  }, [matches.length]);

  const goToNextMatch = useCallback(() => {
    if (!matches.length) return;
    setCurrentMatchIdx((prev) => {
      if (prev < 0) return 0;
      if (prev >= matches.length - 1) return 0;
      return prev + 1;
    });
  }, [matches.length]);

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.shiftKey) {
        goToPrevMatch();
      } else {
        goToNextMatch();
      }
    }
    if (event.key === 'Escape') {
      setSearchText('');
    }
  };

  const handlePrint = useReactToPrint({
    content: () => pdfRef.current,
    pageStyle: `@media print {
      @page {
        size: A4;
        margin: 0;
      }

      .react-pdf__Page__canvas {
        margin: 0 auto !important;

        page-break-before: always;
      }
    }`,
  });

  const textRenderer = useCallback(
    (textItem) => {
      if (!searchText.length) {
        return highlightPattern(textItem.str, null);
      }
      return highlightPattern(textItem.str, searchText);
    },
    [searchText]
  );

  const onSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const clearSearch = () => {
    setSearchText('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Sync main-doc scroll -> thumbnail scroll so the current page stays visible in the thumbs
  const handleMainScroll = () => {
    const container = mainResumeRef.current;
    if (!container) return;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const percent = maxScroll > 0 ? container.scrollTop / maxScroll : 0;

    const thumbs = miniScrollWindow.current;
    if (thumbs) {
      const maxThumbScroll = thumbs.scrollHeight - thumbs.clientHeight;
      if (maxThumbScroll > 0) {
        thumbs.scrollTop = percent * maxThumbScroll;
      }
    }
  };

  // Clicking a thumbnail scrolls the main doc to that page
  const scrollMainToPage = (pageIndex) => {
    const container = mainResumeRef.current;
    if (!container || pageIndex < 0) return;
    const pageElements = container.querySelectorAll('.react-pdf__Page');
    const target = pageElements[pageIndex];
    if (!target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    container.scrollTo({
      top: container.scrollTop + (targetRect.top - containerRect.top),
      behavior: 'smooth',
    });
  };

  // Remember where we were before changing zoom so we can restore the same region.
  const rememberScrollPercent = () => {
    const container = mainResumeRef.current;
    if (!container) return;
    const maxScroll = container.scrollHeight - container.clientHeight;
    pendingScrollPercentRef.current =
      maxScroll > 0 ? container.scrollTop / maxScroll : 0;
  };

  const handleZoomIn = () => {
    const next = ZOOM_LEVELS.find((level) => level > zoom);
    if (next === undefined) return;
    rememberScrollPercent();
    setZoom(next);
  };

  const handleZoomOut = () => {
    const reversed = [...ZOOM_LEVELS].reverse();
    const prev = reversed.find((level) => level < zoom);
    if (prev === undefined) return;
    rememberScrollPercent();
    setZoom(prev);
  };

  const handleZoomReset = () => {
    if (zoom === DEFAULT_ZOOM) return;
    rememberScrollPercent();
    setZoom(DEFAULT_ZOOM);
  };

  // After zoom change, restore the previous scroll percent so the same
  // region remains in view in the rescaled document.
  useEffect(() => {
    const container = mainResumeRef.current;
    if (!container || pendingScrollPercentRef.current === null) {
      return undefined;
    }
    const percent = pendingScrollPercentRef.current;
    pendingScrollPercentRef.current = null;
    const t = setTimeout(() => {
      const c = mainResumeRef.current;
      if (!c) return;
      const maxScroll = c.scrollHeight - c.clientHeight;
      c.scrollTop = percent * maxScroll;
    }, 80);
    return () => clearTimeout(t);
  }, [zoom]);

  const mainWindowScale = zoom;
  const canZoomIn = zoom < MAX_ZOOM;
  const canZoomOut = zoom > MIN_ZOOM;
  const zoomLabel = `${Math.round((zoom / DEFAULT_ZOOM) * 100)}%`;
  const mainWindowWidth = isTabletOrMobile ? mainResumeWidth : '';
  const mainContainerWidth = isModal && '670px';
  const mainContainerHeight = isModal && '496px';

  const matchCounterLabel = useMemo(() => {
    if (!searchText) {
      return '';
    }
    if (!matches.length) {
      return 'No matches';
    }
    return `${currentMatchIdx + 1} of ${matches.length}`;
  }, [searchText, matches.length, currentMatchIdx]);

  const hasNoMatches = !!searchText && matches.length === 0;

  return (
    <div
      className={classNames(classes.Wrapper, {
        [classes.addCandidateModal]: addCandidateModal,
        [classes.hasResume]: !!source,
      })}
    >
      {source && (
        <div className={classes.searchBar}>
          <div
            className={classNames(classes.searchField, {
              [classes.searchFieldError]: hasNoMatches,
            })}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchText}
              onChange={onSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search in resume"
              className={classes.searchInput}
            />
            {matchCounterLabel && (
              <span className={classes.searchCounter}>{matchCounterLabel}</span>
            )}
            {searchText && (
              <button
                type="button"
                onClick={clearSearch}
                className={classes.clearButton}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className={classes.searchControls}>
            <button
              type="button"
              onClick={goToPrevMatch}
              disabled={matches.length === 0}
              className={classes.navButton}
              aria-label="Previous match"
              title="Previous match (Shift+Enter)"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={goToNextMatch}
              disabled={matches.length === 0}
              className={classes.navButton}
              aria-label="Next match"
              title="Next match (Enter)"
            >
              ›
            </button>
          </div>
          <div className={classes.zoomDivider} aria-hidden="true" />
          <div className={classes.zoomControls}>
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={!canZoomOut}
              className={classes.navButton}
              aria-label="Zoom out"
              title="Zoom out"
            >
              −
            </button>
            <button
              type="button"
              onClick={handleZoomReset}
              className={classes.zoomLabel}
              aria-label="Reset zoom"
              title="Reset zoom to 100%"
            >
              {zoomLabel}
            </button>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={!canZoomIn}
              className={classes.navButton}
              aria-label="Zoom in"
              title="Zoom in"
            >
              +
            </button>
          </div>
        </div>
      )}

      <div className={classes.Pdf}>
        <div
          className={classes.resumes}
          ref={miniScrollWindow}
          style={{ height: isModal && '496px' }}
        >
          {source && (
            <Document
              ref={pdfRef}
              file={source}
              className={classes.resume}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {[...Array(numPages)].map((u, page) => (
                <Page
                  pageNumber={page + 1}
                  renderTextLayer={false}
                  renderMode="canvas"
                  renderAnnotationLayer={false}
                  scale={1.8}
                  height={600}
                  className={classes.page}
                  onClick={() => scrollMainToPage(page)}
                  key={page}
                />
              ))}
            </Document>
          )}
        </div>

        {source && (
          <>
            <div
              ref={mainResumeRef}
              className={classes.mainResumeContainer}
              style={{
                height: isTabletOrMobile ? '' : mainContainerHeight,
                width: isTabletOrMobile ? '' : mainContainerWidth,
              }}
              onScroll={handleMainScroll}
            >
              <Document
                file={source}
                className={classes.mainResume}
                externalLinkTarget="_blank"
              >
                {[...Array(numPages)].map((_, page) => (
                  <Page
                    key={page}
                    pageNumber={page + 1}
                    renderTextLayer
                    renderMode="svg"
                    renderAnnotationLayer
                    scale={isTabletOrMobile ? 1 : mainWindowScale}
                    width={mainWindowWidth}
                    className={classes.page}
                    customTextRenderer={textRenderer}
                  />
                ))}
              </Document>
            </div>
            <Icons
              isPossibleToEdit={isPossibleToEdit}
              hideSearch
              url={url}
              handlePrint={handlePrint}
              addCandidateModal={addCandidateModal}
              deleteResume={deleteResume}
              uploadNewResume={uploadNewResume}
              formik={formik}
              resumeFieldName={resumeFieldName}
              isReadOnly={isReadOnly}
              isEdit={isEdit}
              openResumeInput={openResumeInput}
            />
          </>
        )}
      </div>
    </div>
  );
}
